import * as React from "react";
import {useMemo, useState} from "react";
import {Cell, Pie, PieChart, PieLabelRenderProps, ResponsiveContainer, Tooltip} from "recharts";
import {ILoginTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {LOGIN_FREQUENCY_DESCRIPTION, PIE_COLORS, tooltipStyle} from "../shared/analytics.constants";

interface ILoginFrequencySectionProps {
    loginFrequency: ILoginTabResponse["loginFrequency"];
    avgLoginsPerUser: number;
}

export const LoginFrequencySection: React.FC<ILoginFrequencySectionProps> = (props) => {
    const [loginFreqSort, setLoginFreqSort] = useState<{ col: string; dir: SortDir }>({col: "", dir: "desc"});
    const sortedLoginFreq = useMemo(
        () => (loginFreqSort.col
            ? applySort(props.loginFrequency, loginFreqSort.col, loginFreqSort.dir)
            : props.loginFrequency),
        [props.loginFrequency, loginFreqSort]);

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Login Frequency</h2>
                <span className="admin-analytics__section-subtitle">
                    How often do users log in during the selected period?
                </span>
            </div>
            <p className="admin-analytics__section-desc">{LOGIN_FREQUENCY_DESCRIPTION}</p>
            <div className="admin-analytics__frequency-row">
                <div className="admin-analytics__frequency-card">
                    <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                        <div className="admin-analytics__summary-label">Avg Logins per User</div>
                        <div className="admin-analytics__summary-value">{props.avgLoginsPerUser}</div>
                        <div className="admin-analytics__summary-note">across all users</div>
                    </div>
                </div>
                <div className="admin-analytics__frequency-chart">
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie
                                data={props.loginFrequency}
                                dataKey="userCount"
                                nameKey="bucket"
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={110}
                                paddingAngle={2}
                                label={(props: PieLabelRenderProps) => {
                                    const {bucket, percentage} = props.payload as {
                                        bucket: string;
                                        percentage: number
                                    };
                                    return `${bucket} (${percentage}%)`;
                                }}
                            >
                                {props.loginFrequency.map((_, index) => (
                                    <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]}/>
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value, name) => [value as number, `${name} users`]}
                                {...tooltipStyle} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("bucket", "Frequency", loginFreqSort, setLoginFreqSort, undefined, "asc")}
                    {sortTh("userCount", "Users", loginFreqSort, setLoginFreqSort,
                        "admin-analytics__cell--right")}
                    {sortTh("percentage", "% of Total", loginFreqSort, setLoginFreqSort,
                        "admin-analytics__cell--right")}
                </tr>
                </thead>
                <tbody>{sortedLoginFreq.map((row) => (
                    <tr key={row.bucket}>
                        <td>{row.bucket}</td>
                        <td className="admin-analytics__cell--right">{row.userCount}</td>
                        <td className="admin-analytics__cell--right">{row.percentage}%</td>
                    </tr>
                ))}</tbody>
            </table>
        </section>
    );
};
